 
/**
 * Updates a user's profile from the API using their ID and an authentication token.
 *
 * @param {number} id - The ID of the user whose profile is to be updated.
 * @param {string} auth_token - The authentication token for API authorization.
 * @param {Partial<Profile>} profile - A partial profile object containing only the fields to update.
 * @returns {Promise<{success: boolean, message: string, data: any}>} 
 * An object indicating whether the operation was successful, a message, and the updated profile data if successful.
 */

// Function Imports
import { isNotEmptyObject } from '@/lib/functions/isNotEmptyObject';

// Type Imports
import { Profile } from '@/lib/types/profile';

export async function setUserProfile(
  id: number,
  auth_token: string,
  profile: Partial<Profile>
) {

  // Ensure we only send non-empty payloads
  if (!isNotEmptyObject(profile)) {
    return { success: false, message: 'No valid fields to update', data: null };
  }

  try {
    const response = await fetch(`${process.env.NEXT_PUBLIC_SITE_URL}/api/customer/${id}/`, {
      method: 'PUT',
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${auth_token}`,
        "Pragma": "no-cache",
        "Cache-Control": "no-cache, no-store, must-revalidate",
        "Expires": "0",
      },
      cache: "no-store", // Prevent Next.js from caching
      body: JSON.stringify(profile),
    });

    switch(response?.status) {
      case 200:
      case 201: {
        const data = await response.json();
        if (data && isNotEmptyObject(data)) {
          return { 'success': true, 'message': '', 'data': data };
        }
        return { 'success': false, 'message': 'Profile not found in response', 'data': null };
      }
      case 400: {
        const data = await response.json();
        return { 'success': false, 'message': data.error ?? '', 'data': null };
      }
      default: {
        return {'success': false, 'message': 'Unknown error', 'data': null};
      }
    }
  }
  catch {
    return {'success': false, 'message': '', 'data': null};
  }
}
